import { z } from "zod";
import { CreateServicePayloadSchema, ServiceReferenceSchema } from "../service/write-payloads.js";
// ---------------------------------------------------------------------------
// Event write payloads — Zod schemas are the single source of truth so the API
// controllers can validate them at runtime (ZodValidationPipe). `z.object`
// strips unknown keys by default, so server-owned / read-only fields a client
// might send — `deleted`, `archived`, `invoiceId`, `invoice`, `quotation`,
// `notificationSent`, `companyId` and notably `shiftingQuantity` (computed
// server-side by the tour/ORS pass) — never reach the service (mass-assignment
// guard). `manualShifting*` stay in the contract (a solo manager sets them on
// the event form) but a non-manager's values are dropped server-side, since in
// multi-user mode overrides go through the manager-only billing-preview endpoint.
// ---------------------------------------------------------------------------
const EntitySchema = ServiceReferenceSchema;
// A manual travel-fee override: a number, or "no override". The event form
// clears it by sending the empty string "" (the legacy sentinel the old backend
// mapped to null in the entity mapper); accept it and `null`, and normalize both
// to null so the persisted value is always `number | null`.
const ManualShiftingValueSchema = z
    .union([z.number(), z.literal(""), z.null()])
    .transform((value) => (typeof value === "number" ? value : null));
export const EventServiceCreatePayloadSchema = CreateServicePayloadSchema.extend({
    description: ServiceReferenceSchema,
});
const EventWritePayloadBaseSchema = z.object({
    beginTime: z.string(),
    endTime: z.string(),
    manualShiftingAmount: ManualShiftingValueSchema,
    manualShiftingQuantity: ManualShiftingValueSchema,
    plannedServices: z.array(EventServiceCreatePayloadSchema),
    customer: EntitySchema,
    serviceAddressId: z.string().min(1).nullable().optional(),
    // UI flags the event form does not always send (the create form omits `cloned`).
    // Optional, not `.default()`: a shared create/update base must not silently
    // reset an omitted flag on update — the create default is applied in the
    // service instead. Update preserves the persisted value when omitted.
    aggregated: z.boolean().optional(),
    cloned: z.boolean().optional(),
});
// Solo create: memberId is taken from the context on the backend.
export const EventCreateSoloPayloadSchema = EventWritePayloadBaseSchema;
// Multi create: the assigned member is explicit in the payload.
export const EventCreateMultiPayloadSchema = EventWritePayloadBaseSchema.extend({
    memberId: z.string(),
});
// `googleAgendaEventId` is intentionally absent: it is managed server-side by the
// calendar sync (see the controller's updateGoogleAgendaEventId call), never set
// from the request body.
export const UpdateEventPayloadSchema = EventWritePayloadBaseSchema.extend({
    id: z.string(),
    memberId: z.string().optional(),
});
/**
 * `computed` seul — pilote un message annonçant un recalcul ("synchronisés").
 * Tolère l'ancien contrat booléen de `PUT /api/tour` (pré-#843, déploiement en
 * deux temps) en dégradant vers sa sémantique d'origine.
 */
export function tourUpdateComputed(result) {
    return typeof result === "boolean" ? result : result.computed;
}
/**
 * `computed` OU `cleared > 0` — le signal qui doit piloter l'invalidation du
 * cache front. Gater uniquement sur `computed` rate la branche où le serveur
 * a écrit sans recalculer : le cache continue de servir une distance périmée
 * et l'avertissement UI, qui ne s'allume que sur `null`, reste éteint.
 * Même tolérance de contrat que `tourUpdateComputed`.
 */
export function tourUpdateWroteData(result) {
    if (typeof result === "boolean") {
        return result;
    }
    return result.computed || result.cleared > 0;
}
//# sourceMappingURL=event.model.js.map